/**
 * Where the most/least-activity labels sit — the client's R2 item 1, 2026-09-08.
 *
 * The two badges used to be centred on their basin's centroid, which put a pill of type flat
 * on top of the shape it names. The client's instruction is a **call-out**: the label offset
 * from the polygon, outside it where there is room, joined back to the basin by a thin leader
 * line. This module is the arithmetic half of that, kept away from Leaflet on purpose — it
 * takes four rectangles in container pixels and answers with two more, so it can be reasoned
 * about and unit-tested without a map, a tile server or a DOM.
 *
 * ## Why four orthogonal sides and not a free angle
 *
 * A leader at an arbitrary angle needs a rotated element (or an SVG line in its own overlay
 * pane) and a label whose near edge is the *nearest point* on its outline rather than a whole
 * side. Four sides — right, left, above, below — keep the leader a one-pixel box, keep the
 * label centred on the anchor's own axis, and make "the label does not touch the basin" true
 * by construction rather than by search. On eleven basins in one frame that is not a
 * compromise worth paying for.
 *
 * ## The order the sides are tried in is about the chrome, not about taste
 *
 * `#landMapSlot` carries the filter bar top-left, the watershed widget bottom-left, the ticker
 * along the bottom and Leaflet's zoom + attribution bottom-right. Right and up are therefore
 * the two directions with room in them on this particular map, and they are tried first; the
 * obstacle penalty below is what decides the rest, and the preference order only breaks ties.
 *
 * Every rectangle in and out of here is in **container pixels** — the map's own frame, which
 * is what `L.Map.latLngToContainerPoint` answers in — except the two the function returns,
 * which are relative to the anchor because they are written onto a zero-sized marker icon
 * pinned there.
 */

/** A rectangle: top-left corner and size, in container pixels. */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type CalloutSide = 'right' | 'up' | 'left' | 'down';

export interface Callout {
  side: CalloutSide;
  /** The label's top-left, relative to the anchor. */
  label: { x: number; y: number };
  /** The leader line's box, relative to the anchor. One of its dimensions is the line width. */
  leader: Box;
}

export interface PlaceCalloutOptions {
  /** The basin's centroid, where the marker is pinned and where the leader starts. */
  anchor: { x: number; y: number };
  /** The basin's own bounding box — what the label has to stay off. */
  basin: Box;
  /** The measured label, as rendered. */
  label: { w: number; h: number };
  /** The map container. */
  frame: { w: number; h: number };
  /** Anything the label must not cover: the filter bar, the widget, the ticker, the controls. */
  obstacles?: readonly Box[];
  /** Clear air between the basin's edge and the label — the visible length of the leader. */
  gap?: number;
  /** Keep-out from the frame's own edges. */
  margin?: number;
}

/** The leader's thickness. A hairline; the dot at the anchor end is what makes it land. */
export const LEADER_WIDTH = 1.5;

const DEFAULT_GAP = 16;
const DEFAULT_MARGIN = 6;

/** The sides in the order ties are broken — see the module comment. */
export const SIDES: readonly CalloutSide[] = ['right', 'up', 'left', 'down'];

const overlap = (a: Box, b: Box): number =>
  Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)) *
  Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));

/** How far a box hangs outside the frame, summed over the four edges. */
const spill = (box: Box, frame: { w: number; h: number }, margin: number): number =>
  Math.max(0, margin - box.x) +
  Math.max(0, margin - box.y) +
  Math.max(0, box.x + box.w - (frame.w - margin)) +
  Math.max(0, box.y + box.h - (frame.h - margin));

/**
 * The label's box for one side, in absolute container pixels.
 *
 * Along the leader's axis the label is pushed clear of the **basin's bounding box** plus the
 * gap, which is what stops it touching the shape at any zoom. Across that axis it is centred
 * on the **anchor**, not on the box, so that the leader stays orthogonal and unmistakably
 * points at the basin's middle rather than at a corner of its extent.
 */
function labelBox(side: CalloutSide, o: Required<Pick<PlaceCalloutOptions, 'anchor' | 'basin' | 'label'>>, gap: number): Box {
  const { anchor, basin, label } = o;
  if (side === 'right') return { x: basin.x + basin.w + gap, y: anchor.y - label.h / 2, w: label.w, h: label.h };
  if (side === 'left') return { x: basin.x - gap - label.w, y: anchor.y - label.h / 2, w: label.w, h: label.h };
  if (side === 'up') return { x: anchor.x - label.w / 2, y: basin.y - gap - label.h, w: label.w, h: label.h };
  return { x: anchor.x - label.w / 2, y: basin.y + basin.h + gap, w: label.w, h: label.h };
}

/** The leader, absolute: anchor → the label's near edge, on one axis. */
function leaderBox(side: CalloutSide, anchor: { x: number; y: number }, label: Box): Box {
  const half = LEADER_WIDTH / 2;
  if (side === 'right') return { x: anchor.x, y: anchor.y - half, w: Math.max(0, label.x - anchor.x), h: LEADER_WIDTH };
  if (side === 'left') {
    const from = label.x + label.w;
    return { x: from, y: anchor.y - half, w: Math.max(0, anchor.x - from), h: LEADER_WIDTH };
  }
  if (side === 'up') {
    const from = label.y + label.h;
    return { x: anchor.x - half, y: from, w: LEADER_WIDTH, h: Math.max(0, anchor.y - from) };
  }
  return { x: anchor.x - half, y: anchor.y, w: LEADER_WIDTH, h: Math.max(0, label.y - anchor.y) };
}

/**
 * Place one call-out.
 *
 * The score is a cost in square pixels with the frame counted heavily: a label half off the
 * map is worse than a label over the ticker, and both are worse than a label in clear air.
 * The basin's own box is in the sum too — it can only be non-zero when the frame is too small
 * for the label to fit outside the shape at all, and in that case a side that at least reaches
 * open air on one axis is still the better answer than the first one in the list.
 */
export function placeCallout(options: PlaceCalloutOptions): Callout {
  const gap = options.gap ?? DEFAULT_GAP;
  const margin = options.margin ?? DEFAULT_MARGIN;
  const obstacles = options.obstacles ?? [];
  const { anchor, basin, label, frame } = options;

  let best: { side: CalloutSide; box: Box; cost: number } | null = null;
  for (const side of SIDES) {
    const box = labelBox(side, { anchor, basin, label }, gap);
    const cost =
      spill(box, frame, margin) * 1000 +
      overlap(box, basin) * 10 +
      obstacles.reduce((sum, o) => sum + overlap(box, o), 0);
    if (!best || cost < best.cost) best = { side, box, cost };
  }

  const chosen = best!;
  const leader = leaderBox(chosen.side, anchor, chosen.box);
  return {
    side: chosen.side,
    label: { x: chosen.box.x - anchor.x, y: chosen.box.y - anchor.y },
    leader: { x: leader.x - anchor.x, y: leader.y - anchor.y, w: leader.w, h: leader.h },
  };
}

/**
 * The chrome a call-out must keep off, measured against the map container.
 *
 * Read from the DOM rather than passed down as props: the filter bar arrives as `children`,
 * the widget places itself and remembers where it was dragged to, and Leaflet owns the two
 * controls — so the only place all four of them have a position is the document. The rects
 * come back in the map's own container pixels, which is what `placeCallout` takes.
 */
export function chromeBoxes(container: HTMLElement | null, selectors: readonly string[]): Box[] {
  if (!container) return [];
  const frame = container.getBoundingClientRect();
  const root = container.closest('#landMapSlot') ?? container.ownerDocument;
  const boxes: Box[] = [];
  for (const selector of selectors) {
    for (const el of Array.from(root.querySelectorAll<HTMLElement>(selector))) {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      boxes.push({ x: r.x - frame.x, y: r.y - frame.y, w: r.width, h: r.height });
    }
  }
  return boxes;
}
